import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  applySkyTheme,
  easternHour,
  secondsUntilNextHour,
  skyHourKey,
  setSkyTuning,
  getSkyTuning,
} from '../lib/skyTheme.js';
import { effectiveSkyTuning, skyMarkVisible } from '../lib/skyTuning.js';
import { fetchSnapshot } from '../lib/snapshot.js';
import { resolvePds, getRecord } from '../lib/atproto.js';
import { ME_DID, COLLECTIONS } from '../config.js';

/* global __SKY_SEED__ */

const ThemeContext = createContext(null);

// The is.dame.sky/self snapshot baked into the bundle at build (see
// vite.config.js), so main.jsx can paint the tuned hour before React mounts
// and the mark's first render already knows whether it's shown. Null in dev
// without a prefetch; the runtime fetch below covers that case.
export const SKY_SEED = typeof __SKY_SEED__ !== 'undefined' ? __SKY_SEED__ : null;

// The site runs a single, always-on theme: the hour-tracking "sky" mode,
// whose palette is derived from the current sky-avatar frame (see
// src/lib/skyTheme.js). The retired static light/dark themes and their
// switcher have been removed from the chrome — only the sky clock's time
// switcher survives (the hour chip in the bottom bar), which steps the
// palette through the day by hand.
const THEME = 'sky';

// `nudge` is for the user-gesture paths only (see applyThemeColor).
function applyTheme(skyHour, { nudge = false } = {}) {
  document.documentElement.setAttribute('data-theme', THEME);
  const palette = applySkyTheme(skyHour ?? easternHour());
  applyThemeColor(palette.themeColor, { nudge });
}

// Two tunings paint identically when they serialise identically: both come
// out of effectiveSkyTuning, which builds them in the record's own order.
function sameTuning(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

// State for the iOS re-tint scroll nudge below. Module-level so
// overlapping nudges (rapid hour changes — e.g. mashing the sky
// hour chip) share one TRUE baseline: a later nudge must not read the
// 1px-displaced position left by an earlier one as its "original"
// scroll position, or each overlap leaks a permanent 1px downward
// creep when the racing restores land out of order.
let nudgeBaseY = null;
let nudgeRestoreRaf = 0;

function applyThemeColor(color, { nudge = false } = {}) {
  if (typeof document === 'undefined') return;
  const head = document.head;

  const current = head.querySelectorAll('meta[name="theme-color"]');
  const unchanged =
    current.length === 1 && current[0].getAttribute('content') === color;
  if (unchanged) return;
  current.forEach((m) => m.remove());
  const meta = document.createElement('meta');
  meta.setAttribute('name', 'theme-color');
  meta.setAttribute('content', color);
  head.appendChild(meta);

  // iOS Safari re-evaluates its status bar / URL bar tint at the END
  // of a user gesture (touchend). Nudging a 1px scroll provokes the
  // re-tint pass; without this, the chrome stays on the previous
  // hour's color until the next scroll/tap. Re-entrant: the baseline
  // is captured once per settled position and the pending restore is
  // cancelled, so back-to-back nudges always restore to where the page
  // actually started.
  //
  // Only when a gesture asked for it. Applies driven by the clock or by a
  // fetch landing used to nudge too — three times in the first couple of
  // seconds of every load — and a programmatic scroll cancels an iOS
  // momentum scroll the reader has just started. Those tints catch up on
  // the reader's next scroll anyway.
  if (!nudge) return;
  try {
    if (nudgeBaseY == null) nudgeBaseY = window.scrollY || window.pageYOffset || 0;
    window.scrollTo(0, nudgeBaseY + 1);
    cancelAnimationFrame(nudgeRestoreRaf);
    nudgeRestoreRaf = requestAnimationFrame(() => {
      window.scrollTo(0, nudgeBaseY);
      nudgeBaseY = null;
    });
  } catch {
    /* no scrolling to nudge (e.g. a non-browser render) */
  }
}

export function ThemeProvider({ children }) {
  // The site's sky clock. `liveHour` tracks the real Eastern hour; the
  // override (driven by the time-switcher chip in the bottom bar)
  // freezes it on an arbitrary hour so each step can be inspected. The
  // hour drives the sky palette AND the chrome-bar avatar mark (which
  // follows the hourly sky frames).
  const [liveHour, setLiveHour] = useState(() => easternHour());
  const [skyOverride, setSkyOverride] = useState(null);
  const skyHour = skyOverride ?? liveHour;
  // Mirror for callbacks that need the current hour without re-binding.
  const skyHourRef = useRef(skyHour);
  skyHourRef.current = skyHour;

  // Studio preview override for the chrome-bar avatar mark. The admin
  // SkyThemeStudio paints the palette itself (with the in-progress draft
  // tuning) while previewing an hour; this mirrors that hour onto the
  // avatar so the brand mark steps in lockstep with the palette being
  // tuned — without touching the sky clock (skyHour) that drives the
  // bottom-bar chip and the saved-palette apply effect, so the chip's
  // tap-to-advance keeps working and the draft preview isn't clobbered
  // by a saved-tuning re-apply. null = follow the clock (the normal case).
  const [skyPreviewHour, setSkyPreviewHour] = useState(null);
  const skyDisplayHour = skyPreviewHour ?? skyHour;

  // Does the top chrome draw the hourly sky-avatar square beside the site
  // name? Owned by the is.dame.sky/self record's `showMark` (default on, so
  // the mark survives a record written before the field existed), and
  // shadowed while the admin studio has the toggle under the cursor so the
  // owner sees the real chrome answer, not a description of it.
  const [markVisible, setMarkVisible] = useState(() => skyMarkVisible(SKY_SEED));
  const [markPreview, setMarkPreview] = useState(null);
  const showSkyMark = markPreview ?? markVisible;

  // Bumped once the is.dame.sky tuning override resolves (snapshot then
  // live), so the apply effect below re-paints the palette with it. Kept
  // in a ref too, for advanceSkyHour's synchronous apply guard.
  const [tuningRev, setTuningRev] = useState(0);
  const tuningRevRef = useRef(0);
  tuningRevRef.current = tuningRev;

  // What applyTheme last painted. advanceSkyHour applies synchronously
  // (for the iOS same-gesture theme-color) and then sets state; without
  // this guard the state-driven effect would re-apply the identical hour
  // right after — a second scroll nudge per tap. The signature folds in
  // tuningRev so a freshly-loaded override re-applies even on the same hour.
  //
  // Seeded with what main.jsx already painted before React mounted (this
  // hour, with the tuning baked in at build time — rev 0), so mounting
  // doesn't repaint an identical palette.
  const appliedRef = useRef(`sky:${liveHour}:0`);

  useEffect(() => {
    // Skip while the admin SkyThemeStudio is live-painting its own
    // preview — its direct applySkyTheme calls own the palette, and a
    // clock tick here would clobber the selected hour. When the preview
    // clears (studio unmounts), this effect re-runs and catches up to
    // the real clock (or the chip's override) with the saved tuning.
    if (skyPreviewHour != null) return;
    const sig = `sky:${skyHour}:${tuningRev}`;
    if (appliedRef.current !== sig) {
      applyTheme(skyHour);
      appliedRef.current = sig;
    }
  }, [skyHour, tuningRev, skyPreviewHour]);

  // Load the optional is.dame.sky/self override: snapshot for an instant
  // swap-in, then the live record. Installs it globally (setSkyTuning) and
  // bumps tuningRev to re-paint. Absent / disabled / empty → the built-in
  // hourly palette stands, exactly like the nav-menu override.
  //
  // main.jsx has usually installed the build's copy of this same record
  // already, so the common case is that neither answer changes anything —
  // and then nothing repaints. When the live record says the override is
  // off, empty or gone, that has to clear whatever the build baked in:
  // otherwise switching a tuning off in the studio didn't reach visitors
  // until the next rebuild.
  useEffect(() => {
    let cancelled = false;
    const install = (record) => {
      if (cancelled) return;
      // The mark flag is read on its own: a record with `enabled: false`
      // still gets to hide the mark.
      setMarkVisible(skyMarkVisible(record));
      const tuning = effectiveSkyTuning(record);
      if (sameTuning(tuning, getSkyTuning())) return;
      setSkyTuning(tuning);
      setTuningRev((n) => n + 1);
    };
    (async () => {
      const seed = await fetchSnapshot('sky');
      if (seed?.value) install(seed);
      try {
        const pds = await resolvePds(ME_DID);
        const rec = await getRecord(pds, { repo: ME_DID, collection: COLLECTIONS.sky, rkey: 'self' });
        if (rec?.value) install(rec);
      } catch (err) {
        // The PDS answers a missing record with 400 RecordNotFound (some
        // with 404): there is no override, so the built-in palette stands.
        // Anything else is the network — keep what the snapshot gave.
        if (err?.status === 400 || err?.status === 404) install(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Tick the clock over at the top of each Eastern hour (minutes/seconds
  // track UTC, so the boundary is computable locally). The avatar mark
  // and palette follow the hour. Timers sleep in background tabs, so
  // visibility-return also re-reads the clock.
  useEffect(() => {
    let timer;
    const schedule = () => {
      // Small pad past the boundary so a clamped/early timer can't land
      // still inside the old hour and stall until the next one.
      timer = setTimeout(() => {
        setLiveHour(easternHour());
        schedule();
      }, secondsUntilNextHour() * 1000 + 1000);
    };
    schedule();
    const onVisible = () => {
      if (!document.hidden) setLiveHour(easternHour());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Time switcher: step the sky palette forward one hour per call,
  // wrapping at midnight. Wired to the hour chip in the bottom chrome
  // bar (when inside the SkyThemeStudio) so each of the 24 increments
  // can be walked through in place.
  const advanceSkyHour = useCallback(() => {
    const next = (skyHourRef.current + 1) % 24;
    // Sync apply for the same iOS theme-color gesture reason as above.
    applyTheme(next, { nudge: true });
    appliedRef.current = `sky:${next}:${tuningRevRef.current}`;
    setSkyOverride(next);
  }, []);

  // Jump the sky palette to a specific hour (0–23), or pass null to clear
  // the override and return to the live clock. Wired to the SkyHourSheet's
  // 24-hour arc so non-admin visitors can sample any hour's palette. Same
  // sync-apply + appliedRef guard as advanceSkyHour (iOS theme-color).
  const setSkyHour = useCallback((next) => {
    const h = next == null ? easternHour() : next;
    applyTheme(h, { nudge: true });
    appliedRef.current = `sky:${h}:${tuningRevRef.current}`;
    setSkyOverride(next);
  }, []);

  // Install a just-saved is.dame.sky record into the global tuning slot
  // (from the admin studio) so it's in place for when the studio unmounts
  // and the site re-paints on its own. Does NOT bump tuningRev / trigger a
  // re-paint — the studio is live-painting its own preview already, and a
  // rev bump here would make ThemeProvider's apply effect fire at the real
  // clock hour, clobbering the selected-hour preview. Pass null to clear.
  const installSkyTuning = useCallback((record) => {
    setSkyTuning(record ? effectiveSkyTuning(record) : null);
    // The mark is plain state rather than a global slot, so a save has to
    // land here for the chrome to hold the new answer once the studio's
    // preview is dropped. A null record clears back to the default.
    setMarkVisible(record ? skyMarkVisible(record) : true);
  }, []);

  const value = useMemo(
    () => ({
      theme: THEME,
      skyHour,
      skyDisplayHour,
      showSkyMark,
      setSkyMarkPreview: setMarkPreview,
      skyHourKey: skyHourKey(skyHour),
      skyOverridden: skyOverride != null,
      advanceSkyHour,
      setSkyHour,
      setSkyPreviewHour,
      installSkyTuning,
    }),
    [skyHour, skyDisplayHour, showSkyMark, skyOverride, advanceSkyHour, setSkyHour, setSkyPreviewHour, installSkyTuning],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
